import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { formatMoney, moneyLenClass } from './formatMoney'
import type { ZoneWinBanner } from './crashChipLogic'

type Props = Readonly<{
  // The DERIVED banner facts (resolveZoneWinBanner — backend bet fields +
  // the submission-time cashout snapshot, never the frame beacon).
  banner: ZoneWinBanner
  currency: string
  inputPrecision: number
}>

/**
 * The win banner — a chip broken off the won card (which it replaces): the
 * cashed zone converts to a next-round builder underneath while this
 * single-line banner above it carries the win. One-shot 280ms entrance, then
 * stillness; pointer-transparent; dies at race_ready when the round reset
 * clears `myBetIdRef` (derived rendering — no state, no teardown).
 *
 * Layout (v22, ASK 2 — sleeker two-anchor row, SAME on desktop and mobile):
 * the banked amount sits on the LEFT (a slim premium "+" badge, the currency
 * symbol in the Formula face, then the hero digits) and the cashout multiplier
 * "@ 2.31x" rides a FULL-HEIGHT right-hand panel — a right-anchored chip that
 * fills the banner's inner height (bleeds into the right corners, radius-
 * matched) rather than a floating pill. The panel splits the "@" (muted) from
 * the value (bright, $mono) so the multiplier reads as a labelled stat. The
 * v12.3 money-from-bracket ICON is GONE. The amount NEVER truncates:
 * `moneyLenClass` steps the digit font down for a large win in any currency,
 * and the multiplier panel holds its size on the right. Terms unresolvable →
 * no panel, never invented numbers (R3). The redesigned "+" is banner-scoped
 * (`&__plus`) so the shared `.crash-sign` on the busted card is untouched.
 * The aria-label is ALWAYS "Cashed out" — server-auto-cashed bets arrive with
 * status 'won', but it's the same player outcome as a manual cash-out.
 */
export function CrashZoneWinBanner({ banner, currency, inputPrecision }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const symbol = getCurrencySymbol(currency)
  const payoutText = formatMoney(banner.payout, inputPrecision)

  return (
    <div
      className="crash-win-banner"
      role="status"
      aria-live="polite"
      aria-label={t('Cashed out')}
      title={t('Cashed out')}
    >
      <span className="crash-win-banner__amount">
        <span className="crash-win-banner__plus" aria-hidden>
          +
        </span>
        <span className="crash-win-banner__symbol">{symbol}</span>
        <span className={moneyLenClass(payoutText)}>{payoutText}</span>
      </span>
      {banner.multiplier !== undefined && (
        <span className="crash-win-banner__chip">
          <span className="crash-win-banner__chip-at" aria-hidden>
            @
          </span>
          <span className="crash-win-banner__chip-val">{banner.multiplier.toFixed(2)}x</span>
        </span>
      )}
    </div>
  )
}
